import { useState, useEffect, useRef, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { PendingImagesList } from './PendingImagesList.js';
import { Button } from '../ui/Button.js';
import { IconButton } from '../ui/IconButton.js';

// ── Types ───────────────────────────────────────────────────────────────────

export interface PendingImagesDrawerProps {
  projectId: number;
  version?: number;
  pendingCount: number;
  /** True while the pending-images query is loading for the first time (no data yet). */
  isPendingLoading?: boolean;
  onUploaded: () => void;
  /** Show "Generar con IA" button inside the drawer. True when a provider is configured. */
  aiEnabled?: boolean;
  /** Current project status. Passed through to PendingImagesList to block uploads when 'approved'. */
  projectStatus?: string | undefined;
}

interface DrawerTriggerProps {
  pendingCount: number;
  /** True while the pending-images query has no data yet — shows "…" to avoid 0→N flash. */
  isPendingLoading?: boolean | undefined;
  onClick: () => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
}

export interface DrawerSurfaceProps {
  requestClose: () => void;
  children: React.ReactNode;
}

// ── DrawerTrigger ────────────────────────────────────────────────────────────
// Sticky at the bottom of the preview panel's flex column.
// Background covers content that scrolls beneath it.

function DrawerTrigger({ pendingCount, isPendingLoading, onClick, buttonRef }: DrawerTriggerProps) {
  const { t } = useTranslation();
  const allDone = pendingCount === 0;

  // isPendingLoading takes priority: show "…" to avoid the 0→N flash on first load (SUG-1).
  // allDone is only meaningful once data has resolved — never treat loading state as "done".
  const label = isPendingLoading
    ? t('projects.pending.drawer.triggerLoading')
    : allDone
      ? t('projects.pending.drawer.triggerAllUploaded')
      : t('projects.pending.drawer.triggerWithCount', { count: pendingCount });

  const isDimmed = allDone && !isPendingLoading;

  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        display: 'flex',
        justifyContent: 'flex-end',
        padding: 'var(--evo-space-2) 0 var(--evo-space-4)',
        background: 'var(--evo-bg)',
      }}
    >
      <span style={{ opacity: isDimmed ? 0.7 : 1, transition: 'opacity 150ms ease-out' }}>
        <Button
          ref={buttonRef}
          variant="outline"
          tone="muted"
          size="md"
          onClick={onClick}
        >
          {label}
        </Button>
      </span>
    </div>
  );
}

// ── DrawerSurface ────────────────────────────────────────────────────────────
// Absolute-positioned panel scoped to the preview container (position:relative).
// Opens with iOS-like drawer easing (cubic-bezier(0.32, 0.72, 0, 1) per Emil).
// Closes with ease-out before requesting unmount from the parent.

export function DrawerSurface({ requestClose, children }: DrawerSurfaceProps) {
  const { t } = useTranslation();
  const [isEntered, setIsEntered] = useState(false);
  const surfaceRef = useRef<HTMLDivElement>(null);
  // Unique ID for aria-labelledby — avoids collisions if multiple drawers mount.
  const titleId = useId();
  // Stable ref so handleClose's setTimeout always calls the latest requestClose
  // without stale closure issues when the parent re-renders mid-animation.
  const requestCloseRef = useRef(requestClose);
  useEffect(() => { requestCloseRef.current = requestClose; });

  // Two nested rAFs: first rAF commits the initial translateY(100%) paint,
  // second rAF triggers the CSS transition to translateY(0).
  useEffect(() => {
    let id1 = 0;
    let id2 = 0;
    id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setIsEntered(true));
    });
    return () => {
      cancelAnimationFrame(id1);
      cancelAnimationFrame(id2);
    };
  }, []);

  // Exit: animate out (200ms) then call requestClose to unmount.
  // Empty dep array + ref keeps handleClose stable so the keyboard
  // useEffect does not need to re-register on every render.
  const handleClose = useCallback(() => {
    setIsEntered(false);
    setTimeout(() => requestCloseRef.current(), 200);
  }, []);

  // Focus the first interactive element inside the drawer on mount.
  useEffect(() => {
    const el = surfaceRef.current?.querySelector<HTMLElement>(
      'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])',
    );
    el?.focus();
  }, []);

  // Keyboard: Esc to close, Tab to cycle focus within the drawer.
  useEffect(() => {
    function getFocusable(): HTMLElement[] {
      if (!surfaceRef.current) return [];
      return Array.from(
        surfaceRef.current.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])',
        ),
      );
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) { e.preventDefault(); return; }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleClose]);

  // Different transitions for open vs close to feel asymmetric and natural.
  const transition = isEntered
    ? 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1), opacity 180ms ease-out'
    : 'transform 200ms ease-out, opacity 160ms ease-out';

  return (
    <>
      {/* Backdrop: semi-transparent overlay, closes on click */}
      <div
        aria-hidden="true"
        onClick={handleClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.25)',
          opacity: isEntered ? 1 : 0,
          transition: 'opacity 200ms ease-out',
          zIndex: 1,
        }}
      />

      {/* Drawer panel */}
      <div
        ref={surfaceRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '30vh',
          minHeight: '280px',
          maxHeight: '50vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--evo-bg-2)',
          borderTop: '1px solid var(--evo-stroke)',
          boxShadow: '0 -8px 32px rgba(0,0,0,0.20)',
          transform: isEntered ? 'translateY(0)' : 'translateY(100%)',
          opacity: isEntered ? 1 : 0,
          transition,
          zIndex: 2,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--evo-space-3) var(--evo-space-4)',
            borderBottom: '1px solid var(--evo-stroke)',
            flexShrink: 0,
          }}
        >
          <span
            id={titleId}
            style={{
              fontSize: 'var(--evo-fs-label)',
              fontWeight: 'var(--evo-fw-semibold)',
              color: 'var(--evo-fg-1)',
              fontFamily: 'var(--evo-font)',
            }}
          >
            {t('projects.pending.drawer.headerTitle')}
          </span>

          <IconButton
            variant="ghost"
            tone="muted"
            size="sm"
            ariaLabel={t('projects.pending.drawer.closeLabel')}
            icon={<span style={{ fontSize: '18px', lineHeight: 1 }}>×</span>}
            onClick={handleClose}
          />
        </div>

        {/* Scrollable content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '0 var(--evo-space-4)',
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
}

// ── PendingImagesDrawer (export) ─────────────────────────────────────────────

export function PendingImagesDrawer({
  projectId,
  version,
  projectStatus,
  pendingCount,
  isPendingLoading,
  onUploaded,
  aiEnabled = false,
}: PendingImagesDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  function handleOpen() {
    setIsOpen(true);
  }

  // Called by DrawerSurface after the exit animation completes.
  // Unmounts the surface and restores focus to the trigger.
  function handleExited() {
    setIsOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  }

  return (
    <>
      <DrawerTrigger
        pendingCount={pendingCount}
        isPendingLoading={isPendingLoading}
        onClick={handleOpen}
        buttonRef={triggerRef}
      />
      {isOpen && (
        <DrawerSurface requestClose={handleExited}>
          <PendingImagesList
            projectId={projectId}
            {...(version !== undefined ? { version } : {})}
            onUploaded={onUploaded}
            aiEnabled={aiEnabled}
            projectStatus={projectStatus}
          />
        </DrawerSurface>
      )}
    </>
  );
}
